/**
 * @file page-actions.ts
 * @description Действие страницы данными (phase19.31, шаг О4): второстепенная
 * кнопка в верхней панели или пункт меню «⋯».
 *
 * Данными, а не готовой кнопкой: одно и то же действие на широком экране —
 * кнопка-контур рядом с главной, а на телефоне и планшете — пункт «⋯» в
 * панели (места под кнопки там нет). Разметку выбирает `CabinetPageHeader`,
 * страница только перечисляет, что можно сделать.
 *
 * ```ts
 * const refresh: PageAction = { key: "refresh", label: "Обновить", icon: RefreshCw, run: reload };
 * const transfer: PageAction = { key: "transfer", label: "Новое перемещение", icon: ArrowLeftRight, to: "/shop/x/wms/transfers?create=1" };
 * ```
 */

import type { LucideIcon } from "lucide-react";

export interface PageAction {
  key: string;
  label: string;
  /** Подсказка при наведении: «Отменить: добавлен блок «…»». */
  hint?: string;
  icon?: LucideIcon;
  /** Что сделать по нажатию; либо `to` — адрес перехода. */
  run?: () => void;
  to?: string;
  disabled?: boolean;
  /** Идёт выполнение (крутится значок, кнопка заблокирована). */
  loading?: boolean;
  /** Опасное действие — в меню последним, после линии, красным. */
  danger?: boolean;
  /**
   * Что делать с действием на телефоне и планшете: `menu` (по умолчанию) —
   * пункт «⋯»; `hide` — не показывать, потому что оно уже есть в нижней
   * полосе (например, «Обновить список»).
   */
  compact?: "menu" | "hide";
  /**
   * Кнопкой в панели — только значок уже `xl` (1280) или `2xl` (1536):
   * подпись остаётся для скринридера, видна подсказка `hint`. Нужно
   * длинным шапкам (конструктор сайта), чтобы название не сжималось.
   */
  iconOnlyBelow?: "xl" | "2xl";
}

/** Классы подписи кнопки при `iconOnlyBelow` — буквально (Tailwind ищет их в тексте). */
export const ICON_ONLY_LABEL_CLASS: Readonly<Record<NonNullable<PageAction["iconOnlyBelow"]>, string>> = {
  xl: "max-xl:sr-only",
  "2xl": "max-2xl:sr-only",
};

/** Опасные действия — в конец списка (правило «⋯»: опасное последним). */
export function orderPageActions(actions: readonly PageAction[]): PageAction[] {
  return [...actions.filter((a) => !a.danger), ...actions.filter((a) => a.danger)];
}
